import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';

import { Subscription, filter, of } from 'rxjs';
import moment from 'moment-timezone';

import { EventsOverTimePoint } from '../../../TargetingShared/model/statisticsReport/events.over.time.api.model';
import { GaugeChartOptionsModel } from '../../../Charts/models/gaugeChart/gauge-chart-options.model';
import { GaugeChartModel } from '../../../Charts/models/gaugeChart/gauge-chart.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DistributionQuotaInfo } from '../../models/distribution.quota.info';
import { AccountModel } from '../../../../Core/Users/models/account.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';

@Component({
    selector: 'agpr-distribution-usage-chart',
    templateUrl: './distribution-usage-chart.component.html',
    styleUrls: ['./distribution-usage-chart.component.scss'],
    standalone: false
})
export class DistributionUsageChartComponent implements OnChanges, OnDestroy {
    @Input() totalUsagePercentage: number;
    @Input() accountInfo: AccountModel;
    @Input() resetDate: Date;
    @Input() quotaInfo: DistributionQuotaInfo;
    @Input() quotaOverTime: EventsOverTimePoint[];
    @Output() openBuyQuotaDialog = new EventEmitter();

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverities = messageSeverityEnum;

    totalUsageChart: GaugeChartModel;
    monthlyUsageChart: GaugeChartModel;

    dateFormat: string;
    subscriptions: Subscription[] = [];

    constructor(
        private localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => this.dateFormat = prefs.dateFormat)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.buildGaugeCharts();
    }

    buildGaugeCharts() {

        const bigChartOptions: GaugeChartOptionsModel = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentageFontSize: 21,
            percentageFontWeight: '600',
            useOneColor: true
        };

        this.totalUsageChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            label: this.localizationService.transform('Discover.distributionDashboard.chart.used'),
                            xValue: this.totalUsagePercentage,
                        }
                    ]
                )
            }
        ], bigChartOptions);

        const smallChartOptions: GaugeChartOptionsModel = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentageFontSize: 14,
            percentageFontWeight: '600',
            useOneColor: true,
            innerRadiusPercentage: 80
        };

        this.monthlyUsageChart = new GaugeChartModel([
            {
                label: '',
                dataProvider: of(
                    [
                        {
                            xValue: this.calculateMonthlyUsage()
                        }
                    ]
                )
            }
        ], smallChartOptions);
    }

    private calculateMonthlyUsage(): number {
        if (!this.quotaInfo) {
            return 0;
        }

        const numberOfMonthsSinceContractStart = Math.ceil(moment(new Date()).diff(moment(this.accountInfo.startDate), 'months', true)) || 1;
        const contractLengthMonth = Math.ceil(moment(this.accountInfo.expiryDate).subtract(1, 'days').diff(moment(this.accountInfo.startDate), 'months', true));
        return (this.quotaInfo.usedDistributions / numberOfMonthsSinceContractStart)
            / (this.quotaInfo.totalDistributions / contractLengthMonth || 1) * 100;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
